<script setup lang="ts">
import { ref, watch } from 'vue'

import BaseToolbox from '@/components/ui/BaseToolbox.vue'
import { ROUTING_PROFILES } from '@/services/routingService'
import { useUiStore } from '@/stores/uiStore'

defineOptions({ name: 'TrackHelperPointsToolbox' })

const props = defineProps<{
  isOpen: boolean
  pointCount: number
  minPointCount: number
  maxPointCount: number
  isApplying: boolean
}>()

const emit = defineEmits<{
  close: []
  confirm: []
  'update:pointCount': [value: number]
  'routing-profile-change': [profile: string]
}>()

const uiStore = useUiStore()
const isCollapsed = ref(false)

const handlePointCountChange = (event: Event) => {
  emit('update:pointCount', Number((event.target as HTMLInputElement).value))
}

watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen) {
      isCollapsed.value = false
    }
  },
  { immediate: true },
)
</script>

<template>
  <BaseToolbox
    :is-open="props.isOpen"
    :is-collapsed="isCollapsed"
    :close-disabled="props.isApplying"
    title="Track helper points"
    data-testid="track-helper-toolbox"
    @update:is-collapsed="isCollapsed = $event"
    @close="emit('close')"
  >
    <div data-testid="track-helper-toolbox-body">
      <p class="text-sm leading-6 text-gray-600">
        This track has too few route points for local edits. Add helper points first so later
        changes affect only the nearby section while the current track stays visible as reference.
      </p>

      <div class="space-y-3">
        <div class="space-y-1">
          <label class="text-sm font-medium text-gray-900" for="track-helper-routing-profile">
            Routing profile
          </label>
          <select
            id="track-helper-routing-profile"
            :value="uiStore.routingProfile"
            class="w-full rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500"
            @change="emit('routing-profile-change', ($event.target as HTMLSelectElement).value)"
          >
            <option v-for="profile in ROUTING_PROFILES" :key="profile.id" :value="profile.id">
              {{ profile.label }}
            </option>
          </select>
        </div>

        <div class="flex items-center justify-between gap-4">
          <label class="text-sm font-medium text-gray-900" for="track-helper-point-count">
            Number of points
          </label>
          <span class="text-sm font-semibold text-gray-700">{{ props.pointCount }}</span>
        </div>

        <input
          id="track-helper-point-count"
          class="w-full accent-primary-600"
          type="range"
          :min="props.minPointCount"
          :max="props.maxPointCount"
          :step="1"
          :value="props.pointCount"
          :disabled="props.isApplying"
          @input="handlePointCountChange"
        />
      </div>

      <div class="flex justify-end gap-3">
        <button
          type="button"
          class="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
          :disabled="props.isApplying"
          @click="emit('close')"
        >
          Close
        </button>
        <button
          type="button"
          class="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
          :disabled="props.isApplying"
          @click="emit('confirm')"
        >
          Add helper points
        </button>
      </div>
    </div>
  </BaseToolbox>
</template>
